/**
 * Grid 4×N kategorii. Filtrowane po type (income|expense).
 * Selected: ring-2 + bg z color/15. Tap = onChange(id) + haptic light.
 */
import { useMemo } from 'react';
import type { Category } from '../../features/categories/categoriesStore';
import { getCategoryIcon } from '../../lib/categoryIcons';
import { safeColor, haptic } from '../../lib/utils';

interface Props {
  categories: Category[];
  type: 'income' | 'expense';
  value: string | null;
  onChange: (id: string) => void;
}

export function CategoryPicker({ categories, type, value, onChange }: Props) {
  const filtered = useMemo(
    () => categories.filter((c) => c.type === type).sort((a, b) => a.order - b.order),
    [categories, type],
  );

  if (filtered.length === 0) {
    return (
      <p className="text-center text-sm text-slate-500 dark:text-slate-400 py-6">
        Brak kategorii — dodaj w Ustawieniach
      </p>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-2">
      {filtered.map((c) => {
        const Icon = getCategoryIcon(c.icon);
        const color = safeColor(c.color, '#64748b');
        const selected = c.id === value;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              haptic('light');
              onChange(c.id);
            }}
            aria-pressed={selected}
            className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all min-h-[5.5rem] ${
              selected
                ? 'border-sky-500 dark:border-sky-400 ring-2 ring-sky-500/20 bg-sky-50/50 dark:bg-sky-500/10'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
          >
            <span
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${color}22`, color }}
            >
              <Icon size={20} />
            </span>
            <span className="text-[11px] font-medium text-slate-700 dark:text-slate-200 leading-tight text-center line-clamp-2">
              {c.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}
